import type { TwinState } from './types';
import { buildSeedState, TWIN_STATE_VERSION, TWIN_STORAGE_KEY } from './seed';

/**
 * localStorage-backed persistence for the Virtual Twin.
 *
 * This is the demo stand-in for the production database. On first load (or when
 * the persisted version is stale / corrupt) we seed from the canonical seed data.
 * The store is the only thing that touches localStorage; the business-state
 * interface (./index.ts) reads through here so the rest of the app never sees
 * storage details — the same seam that would later swap to a real API.
 */

let memoryFallback: TwinState | null = null;

function hasLocalStorage(): boolean {
  try {
    return typeof window !== 'undefined' && !!window.localStorage;
  } catch {
    return false;
  }
}

function isValidState(value: unknown): value is TwinState {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Partial<TwinState>;
  return (
    candidate.version === TWIN_STATE_VERSION &&
    Array.isArray(candidate.categories) &&
    Array.isArray(candidate.menu_items) &&
    Array.isArray(candidate.menu_item_options)
  );
}

/** Read the twin state, seeding + persisting on first run. */
export function loadState(): TwinState {
  if (!hasLocalStorage()) {
    if (!memoryFallback) memoryFallback = buildSeedState();
    return memoryFallback;
  }

  try {
    const raw = window.localStorage.getItem(TWIN_STORAGE_KEY);
    if (raw) {
      const parsed: unknown = JSON.parse(raw);
      if (isValidState(parsed)) return parsed;
    }
  } catch {
    // fall through to re-seed on parse/read error
  }

  const seeded = buildSeedState();
  saveState(seeded);
  return seeded;
}

/** Persist the twin state. */
export function saveState(state: TwinState): void {
  if (!hasLocalStorage()) {
    memoryFallback = state;
    return;
  }
  try {
    window.localStorage.setItem(TWIN_STORAGE_KEY, JSON.stringify(state));
  } catch {
    memoryFallback = state;
  }
}

/** Wipe persisted state and re-seed from canonical data. Handy for demos. */
export function resetState(): TwinState {
  const seeded = buildSeedState();
  saveState(seeded);
  return seeded;
}
